import { useEffect, useState } from 'react';
import { Plus, Trash2, X } from 'lucide-react';
import type { CategoriaVista, GrupoDeVentaVista } from '@shared/types';
import { etiquetaDePorcion, revisarPorciones } from '@shared/porciones';
import { centavosAPesos, pesosACentavos } from '@shared/centavos';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { UNIDADES } from './AltaConFormatos';
import { fraccionDeFormatos, fraccionMasFina } from '@shared/fraccionInsumo';
import {
  aCantidadBase,
  aCantidadCapturada,
  esUnidadContinua,
  unidadDeCaptura,
} from '@shared/medidas';
import { PrecioPorUnidad, precioDeReferencia } from './PrecioPorUnidad';
import { NotaDeTamanos } from './NotaDeTamanos';

/**
 * Sprint 84 — Editar el producto **como se dio de alta**.
 *
 * El caso que reportó el dueño, palabra por palabra: capturó "Frijoles Charros" con 1 lt y 1/2 lt,
 * *"luego se da cuenta que siempre sí necesita 3/4 lt y 1/4 lt… PERO YA NO PUEDE EDITAR el producto
 * agrupado como lo vio durante la primera vez que lo dio de alta"*.
 *
 * Esta pantalla es deliberadamente **la misma tabla del alta**: quien capturó cuatro tamaños hace
 * una semana reconoce esto sin que nadie le explique nada. Cambiar de lenguaje entre crear y
 * editar es lo que rompió la lógica la primera vez.
 *
 * ---
 *
 * Sprint 87 — **Y el nombre. Y la unidad.**
 *
 * El 84 dejó fuera el nombre a propósito, y lo dejó escrito aquí mismo: *"renombrar afecta a los
 * tickets ya impresos"*. Era cierto por dentro y **indefendible por fuera**. Reporte del dueño:
 *
 * > *"le hacemos la vida imposible al cliente… resulta que hubo un error de dedo y dio de alta
 * > 'GUACAMOLO' en vez de 'GUACAMOLE' y ahora no lo puede cambiar… triste. Quizá le falten las
 * > medidas."*
 *
 * Tenía razón en las dos cosas. El arreglo no fue aflojar la regla sino quitarle el motivo: desde
 * este sprint **la venta guarda el nombre con el que se vendió**, así que el ticket de ayer ya no
 * depende del catálogo de hoy. La historia se defiende sola y el menú se corrige.
 */

interface FormatoEditable {
  productoId?: number;
  etiqueta: string;
  cantidad: string;
  precio: string;
  /** Lo trae el grupo: sirve para avisar que un tamaño oculto sigue existiendo. */
  habilitado: boolean;
}

export function EditarGrupoModal({
  grupo,
  categorias,
  onCerrar,
  onGuardado,
}: {
  grupo: GrupoDeVentaVista | null;
  categorias: CategoriaVista[];
  onCerrar: () => void;
  onGuardado: () => void;
}) {
  const [nombre, setNombre] = useState('');
  const [unidad, setUnidad] = useState('kg');
  const [merma, setMerma] = useState('0');
  const [formatos, setFormatos] = useState<FormatoEditable[]>([]);
  const [categoriaId, setCategoriaId] = useState<string>('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!grupo) return;
    setNombre(grupo.nombre);
    setUnidad(grupo.unidad);
    setMerma(String(grupo.mermaCoccionPct ?? 0));
    setFormatos(
      grupo.formatos.map((f) => ({
        productoId: f.productoId,
        // Del nombre completo se quita el del grupo: en la tabla se edita el tamaño, no la frase.
        etiqueta: f.nombre.startsWith(grupo.nombre)
          ? f.nombre.slice(grupo.nombre.length).trim() || etiquetaDePorcion(f.cantidad, grupo.unidad)
          : f.nombre,
        // Sprint 127 — Lo guardado está en la unidad base; aquí se teclea en gramos o mililitros.
        cantidad: String(aCantidadCapturada(f.cantidad, grupo.unidad)),
        precio: String(centavosAPesos(f.precio)),
        habilitado: f.habilitado,
      })),
    );
    setCategoriaId(grupo.categoriaId ? String(grupo.categoriaId) : '');
    setError(null);
    setGuardando(false);
  }, [grupo]);

  if (!grupo) return null;

  const num = (t: string) => {
    const n = Number((t ?? '').replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  };

  const cambiaUnidad = unidad !== grupo.unidad;
  const renombra = nombre.trim() !== grupo.nombre;
  /**
   * Sprint 87 — Lo que se vende **sin control de existencias** (una salsa que nunca se acaba, un
   * servicio) no tiene ingrediente detrás, así que no tiene tamaños que editar. Pero sí tiene
   * nombre, y era el único que ni siquiera podía abrir esta ventana: el botón se escondía cuando
   * `llevaInventario` era falso. Un typo ahí era igual de definitivo.
   */
  const esSuelto = grupo.insumoId === null;

  const captura = unidadDeCaptura(unidad);

  const paraValidar = formatos.map((f) => ({
    etiqueta: f.etiqueta,
    cantidad: aCantidadBase(num(f.cantidad), unidad),
    precio: pesosACentavos(num(f.precio)),
  }));
  /**
   * Sprint 127 — La divisibilidad sale de los tamaños que hay en la tabla, igual que en el alta.
   *
   * Es lo que cierra el caso que el Sprint 109 sólo destrabó a medias: agregar el renglón "Medio"
   * **es** decir que se parte. No hay una segunda pantalla que tenga que autorizarlo, ni un valor
   * inventado contra el que validar — que fue exactamente el origen del rechazo *"0.5 no es una
   * cantidad que este ingrediente permita"*.
   */
  const deducida = fraccionDeFormatos(
    paraValidar.map((f) => f.cantidad),
    unidad,
  );
  /* La misma regla del servidor: al cambiar de unidad se recalcula; si no, sólo puede aflojarse. */
  const fraccionMinima = cambiaUnidad
    ? deducida
    : fraccionMasFina(deducida, grupo.fraccionMinima);
  const problemas = revisarPorciones(paraValidar, unidad, fraccionMinima);
  const referenciaPorUnidad = precioDeReferencia(paraValidar);

  /**
   * Cambiar la unidad re-escala lo tecleado, no lo reinterpreta.
   *
   * Sin esto, pasar de kilos a piezas convertiría "500 g" en "500 piezas" con sólo tocar un
   * desplegable. El valor real (0.5) se conserva y se vuelve a expresar en la unidad de captura
   * nueva.
   */
  const cambiarUnidad = (nueva: string) => {
    setFormatos((prev) =>
      prev.map((f) => ({
        ...f,
        cantidad: f.cantidad.trim()
          ? String(aCantidadCapturada(aCantidadBase(num(f.cantidad), unidad), nueva))
          : f.cantidad,
      })),
    );
    setUnidad(nueva);
  };

  const guardar = async () => {
    setError(null);
    if (nombre.trim().length < 2) {
      setError('¿Cómo se llama lo que vendes?');
      return;
    }
    const bloqueante = problemas.find((p) => p.bloquea);
    if (bloqueante) {
      setError(`Tamaño ${bloqueante.indice + 1}: ${bloqueante.mensaje}`);
      return;
    }
    setGuardando(true);

    if (esSuelto) {
      const productoId = grupo.formatos[0]?.productoId;
      if (productoId === undefined) {
        setGuardando(false);
        return;
      }
      const suelto = await window.api.productos.actualizar(productoId, {
        nombre: nombre.trim(),
        precio: paraValidar[0].precio,
        categoriaId: categoriaId ? Number(categoriaId) : null,
      });
      setGuardando(false);
      if (!suelto.ok) {
        setError(traducirErrorBackend(suelto.error, suelto.codigo));
        return;
      }
      if (renombra) {
        await window.alertar({
          titulo: `"${nombre.trim()}" actualizado`,
          mensaje: `Antes se llamaba "${grupo.nombre}".`,
          variant: 'success',
        });
      }
      onGuardado();
      return;
    }

    const res = await window.api.productos.actualizarGrupo({
      // Ya se descartó el caso suelto arriba: aquí siempre hay ingrediente.
      insumoId: grupo.insumoId as number,
      categoriaId: categoriaId ? Number(categoriaId) : undefined,
      nombre: nombre.trim(),
      unidad,
      mermaCoccionPct: num(merma),
      // La divisibilidad no viaja: el servidor la deduce de estos mismos tamaños (Sprint 127).
      formatos: formatos.map((f, i) => ({
        productoId: f.productoId,
        etiqueta: paraValidar[i].etiqueta,
        cantidad: paraValidar[i].cantidad,
        precio: paraValidar[i].precio,
      })),
    });
    setGuardando(false);
    if (!res.ok) {
      setError(traducirErrorBackend(res.error, res.codigo));
      return;
    }

    const { agregados, ocultados, borrados, renombradoDesde } = res.data;
    const partes: string[] = [];
    if (renombradoDesde) partes.push(`Antes se llamaba "${renombradoDesde}"`);
    if (agregados > 0) partes.push(`${agregados} tamaño${agregados === 1 ? '' : 's'} nuevo${agregados === 1 ? '' : 's'}`);
    if (ocultados > 0)
      partes.push(
        `${ocultados} se ocult${ocultados === 1 ? 'ó' : 'aron'} (ya se hab${ocultados === 1 ? 'ía' : 'ían'} vendido, así que la historia se conserva)`,
      );
    if (borrados > 0) partes.push(`${borrados} se borr${borrados === 1 ? 'ó' : 'aron'}`);
    if (partes.length > 0) {
      await window.alertar({
        titulo: `"${nombre.trim()}" actualizado`,
        mensaje: partes.join(' · '),
        variant: 'success',
      });
    }
    onGuardado();
  };

  const cambiar = (i: number, campo: 'etiqueta' | 'cantidad' | 'precio', valor: string) => {
    setFormatos((prev) =>
      prev.map((f, idx) => {
        if (idx !== i) return f;
        const siguiente = { ...f, [campo]: valor };
        // Igual que en el alta: la etiqueta se re-sugiere mientras nadie la haya escrito a mano.
        const etiquetaDe = (t: string) => etiquetaDePorcion(aCantidadBase(num(t), unidad), unidad);
        if (campo === 'cantidad' && f.etiqueta === etiquetaDe(f.cantidad)) {
          siguiente.etiqueta = etiquetaDe(valor);
        }
        return siguiente;
      }),
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-auto bg-black/50 p-4">
      <div className="card my-4 w-full max-w-2xl">
        <div className="mb-4 flex items-start justify-between gap-2">
          <div>
            <h2 className="text-xl font-bold">{grupo.nombre}</h2>
            <p className="text-sm text-slate-500">
              {grupo.disponible !== null && `Quedan ${grupo.disponible} ${grupo.unidad}`}
            </p>
          </div>
          <button onClick={onCerrar} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-4">
          {/*
            El nombre va primero y es un campo normal, no un título de adorno: es lo que el negocio
            viene a corregir. Antes ni siquiera aparecía.
          */}
          <div>
            <label className="label" htmlFor="grupoNombre">
              ¿Cómo se llama?
            </label>
            <input
              id="grupoNombre"
              className="input text-lg"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              maxLength={60}
            />
            {renombra && (
              <p className="mt-1 text-xs text-slate-600">
                Se corrige en el menú y en la cocina. Los tickets ya cobrados conservan el nombre
                con el que se vendieron.
              </p>
            )}
          </div>

          <div className={`grid grid-cols-1 gap-3 ${esSuelto ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
            {/* Un producto suelto se vende tal cual: no hay unidad que medir ni merma que estimar. */}
            {esSuelto ? (
              <div>
                <label className="label" htmlFor="grupoPrecio">
                  Precio
                </label>
                <input
                  id="grupoPrecio"
                  className="input"
                  inputMode="decimal"
                  value={formatos[0]?.precio ?? ''}
                  onChange={(e) => cambiar(0, 'precio', e.target.value)}
                />
              </div>
            ) : (
            <div>
              <label className="label" htmlFor="grupoUnidad">
                ¿En qué se mide?
              </label>
              <select
                id="grupoUnidad"
                className="input"
                value={unidad}
                onChange={(e) => cambiarUnidad(e.target.value)}
              >
                {UNIDADES.map((u) => (
                  <option key={u.valor} value={u.valor}>
                    {u.etiqueta}
                  </option>
                ))}
              </select>
            </div>
            )}

            {!esSuelto && (
            <div>
              <label className="label" htmlFor="grupoMerma">
                Merma (%)
              </label>
              <input
                id="grupoMerma"
                className="input"
                inputMode="decimal"
                value={merma}
                onChange={(e) => setMerma(e.target.value)}
              />
            </div>
            )}

            <div>
              <label className="label" htmlFor="grupoCategoria">
                Categoría
              </label>
              <select
                id="grupoCategoria"
                className="input"
                value={categoriaId}
                onChange={(e) => setCategoriaId(e.target.value)}
              >
                <option value="">— Sin categoría —</option>
                {categorias.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/*
            Sprint 127 — Aquí estaba "¿Se vende partido?" (Sprint 109). Se retiró en las dos
            puertas del catálogo a la vez, que es la lección que este proyecto ya pagó: una regla
            que vive en el alta y no en la edición deja al negocio con la mitad del sistema
            diciendo una cosa y la otra mitad otra.
          */}

          {/*
            Dos avisos que sólo salen cuando aplican. Cambiar la unidad no reinterpreta lo que hay
            en el almacén —los números son los mismos— pero el operador tiene que saberlo antes de
            guardar, no descubrirlo en el inventario.
          */}
          {cambiaUnidad && grupo.disponible !== null && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Vas a medirlo en <b>{unidad}</b>. Las cantidades no cambian: lo que hoy son{' '}
              {grupo.disponible} {grupo.unidad} pasarán a leerse como {grupo.disponible} {unidad}.
              Si el número ya no corresponde, corrígelo en Inventario.
            </p>
          )}
          {num(merma) !== (grupo.mermaCoccionPct ?? 0) && (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              La merma nueva aplica a lo que cargues de aquí en adelante. Lo que ya está en el
              almacén no se recalcula.
            </p>
          )}

          {!esSuelto && (
          <div>
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <span className="label mb-0">
                ¿En qué tamaños lo vendes?
              </span>
              <span className="text-xs text-slate-500">Cada renglón es un botón del menú</span>
            </div>

            <div className="space-y-2">
              {formatos.map((f, i) => {
                const problema = problemas.find((p) => p.indice === i);
                return (
                  <div
                    key={f.productoId ?? `nuevo-${i}`}
                    className={`grid grid-cols-1 gap-2 rounded-lg border p-2 sm:grid-cols-[1fr_1fr_1fr_auto] ${
                      problema?.bloquea ? 'border-rose-300 bg-rose-50' : 'border-slate-200'
                    }`}
                  >
                    <div>
                      <label className="text-xs text-slate-600" htmlFor={`grupoCantidad${i}`}>
                        ¿Cuánto lleva? ({captura.nombre})
                      </label>
                      <input
                        id={`grupoCantidad${i}`}
                        className="input"
                        inputMode="decimal"
                        value={f.cantidad}
                        onChange={(e) => cambiar(i, 'cantidad', e.target.value)}
                        placeholder={esUnidadContinua(unidad) ? '500' : '1'}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600" htmlFor={`grupoEtiqueta${i}`}>
                        Cómo se llama
                      </label>
                      <input
                        id={`grupoEtiqueta${i}`}
                        className="input"
                        value={f.etiqueta}
                        onChange={(e) => cambiar(i, 'etiqueta', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-slate-600" htmlFor={`grupoFormatoPrecio${i}`}>
                        Precio
                      </label>
                      <input
                        id={`grupoFormatoPrecio${i}`}
                        className="input"
                        inputMode="decimal"
                        value={f.precio}
                        onChange={(e) => cambiar(i, 'precio', e.target.value)}
                      />
                      <PrecioPorUnidad
                        precioCentavos={paraValidar[i].precio}
                        cantidadBase={paraValidar[i].cantidad}
                        unidad={unidad}
                        referenciaCentavos={referenciaPorUnidad}
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        type="button"
                        onClick={() => setFormatos((prev) => prev.filter((_, x) => x !== i))}
                        className="btn-ghost min-h-[44px] px-2 text-rose-700"
                        aria-label="Quitar tamaño"
                        disabled={formatos.length === 1}
                        title={
                          formatos.length === 1
                            ? 'Deja al menos un tamaño'
                            : 'Quitar este tamaño del menú'
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    {!f.habilitado && (
                      <p className="text-xs text-slate-500 sm:col-span-4">
                        Este tamaño está oculto en el punto de venta.
                      </p>
                    )}
                    {problema && (
                      <p
                        className={`sm:col-span-4 text-xs ${
                          problema.bloquea ? 'font-medium text-rose-700' : 'text-amber-700'
                        }`}
                      >
                        {problema.bloquea ? '⚠ ' : '· '}
                        {problema.mensaje}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* El botón que faltaba: aquí es donde se busca "también lo vendo en 3/4". */}
            <button
              type="button"
              onClick={() =>
                setFormatos((prev) => [
                  ...prev,
                  { etiqueta: '', cantidad: '', precio: '', habilitado: true },
                ])
              }
              className="btn-ghost mt-2 min-h-[44px] w-full justify-center border border-dashed border-slate-300"
            >
              <Plus className="h-4 w-4" />
              Agregar otro tamaño
            </button>

            <NotaDeTamanos unidad={unidad} fraccionMinima={fraccionMinima} />
          </div>
          )}

          {!esSuelto && (
            <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-600">
              Si quitas un tamaño que <b>ya se vendió</b>, no se borra: se oculta del punto de venta
              y las ventas anteriores se conservan.
            </p>
          )}

          {error && (
            <div className="error-text" role="alert">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button onClick={onCerrar} className="btn-ghost" disabled={guardando}>
              Cancelar
            </button>
            <button onClick={guardar} className="btn-primary min-h-[44px]" disabled={guardando}>
              {guardando ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
